import {LockIcon} from '@primer/octicons-react'
import {ActionList, Banner} from '@primer/react'
import {Suspense, useCallback} from 'react'
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import type {LazyRelationshipsBlockingListViewRelayFragment$data} from './__generated__/LazyRelationshipsBlockingListViewRelayFragment.graphql'
import type {LazyRelationshipsBlockingListViewRelayQuery} from './__generated__/LazyRelationshipsBlockingListViewRelayQuery.graphql'
import {
  PAGE_SIZE,
  type PaginatedRelationshipsListAllViewItemFragment,
  RelationshipsListAllViewPaginated,
  RelationshipsListItem,
  RelationshipsListViewLoadingSkeleton,
} from './PaginatedRelationshipsListAllView'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const LazyRelationshipsBlockingListViewRelayFragment = graphql`
  fragment LazyRelationshipsBlockingListViewRelayFragment on Issue
  @refetchable(queryName: "LazyRelationshipsBlockingListViewRelayPaginatedQuery")
  @argumentDefinitions(cursor: {type: "String", defaultValue: null}, pageSize: {type: "Int"}) {
    repository {
      nameWithOwner
    }
    blocking(first: $pageSize, after: $cursor) @connection(key: "LazyRelationshipsBlockingListViewFragment__blocking") {
      edges {
        node {
          id
          url
          number
          titleHTML
          state
          stateReason
          repository {
            name
            nameWithOwner
            owner {
              login
            }
          }
          assignees(first: 10) {
            totalCount
            edges {
              node {
                id
                login
                avatarUrl
              }
            }
          }
          closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
            totalCount
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const LazyRelationshipsBlockingListViewRelayQueryGraphQL = graphql`
  query LazyRelationshipsBlockingListViewRelayQuery($id: ID!, $cursor: String, $pageSize: Int) {
    issue: node(id: $id) {
      ...LazyRelationshipsBlockingListViewRelayFragment @arguments(cursor: $cursor, pageSize: $pageSize)
    }
  }
`

type LazyRelationshipsBlockingListViewRelayProps = {
  itemId: string
  blockingCount: number
}

export function LazyRelationshipsBlockingListViewRelay({
  itemId,
  blockingCount,
}: LazyRelationshipsBlockingListViewRelayProps) {
  return (
    <Suspense fallback={<RelationshipsListViewLoadingSkeleton />}>
      <RelationshipsBlockingListViewRelayInternal itemId={itemId} pageSize={PAGE_SIZE} blockingCount={blockingCount} />
    </Suspense>
  )
}

type RelationshipsBlockingListViewRelayProps = {
  itemId: string
  pageSize: number
  blockingCount: number
}

function RelationshipsBlockingListViewRelayInternal({
  itemId,
  pageSize,
  blockingCount,
}: RelationshipsBlockingListViewRelayProps) {
  const queryData = useLazyLoadQuery<LazyRelationshipsBlockingListViewRelayQuery>(
    LazyRelationshipsBlockingListViewRelayQueryGraphQL,
    {id: itemId, pageSize},
  )

  const issueFragment = queryData.issue
  const {data, loadNext, isLoadingNext} = usePaginationFragment(
    LazyRelationshipsBlockingListViewRelayFragment,
    issueFragment,
  )
  const issueData = data as LazyRelationshipsBlockingListViewRelayFragment$data | null
  const onLoadMore = useCallback(() => loadNext(PAGE_SIZE), [loadNext])

  if (!issueFragment || !issueData?.repository || !issueData.blocking?.edges) {
    return (
      <Banner variant="info">
        <Banner.Title>{LABELS.emptyBlockingList}</Banner.Title>
      </Banner>
    )
  }

  const nodes = issueData.blocking.edges.map(edge => edge?.node).filter(node => !!node)
  if (nodes.length === 0) {
    return (
      <Banner variant="info">
        <Banner.Title>{LABELS.emptyBlockingList}</Banner.Title>
      </Banner>
    )
  }

  const hasNextPage = issueData.blocking.pageInfo.hasNextPage
  const hasInaccessibleBlockingIssues = nodes.length < blockingCount && !hasNextPage

  return (
    <RelationshipsListAllViewPaginated
      accessibleTitle={LABELS.relationNames.blockingIssues}
      isLoadingNext={isLoadingNext}
      hasNextPage={hasNextPage}
      onLoadMore={onLoadMore}
    >
      {nodes.map(node => (
        <RelationshipsListItem
          key={node.id}
          issue={node as PaginatedRelationshipsListAllViewItemFragment}
          sourceIssueRepositoryNameWithOwner={issueData.repository.nameWithOwner}
        />
      ))}
      {hasInaccessibleBlockingIssues && (
        <ActionList.Item disabled aria-disabled="true" className="tmp-pl-3">
          <ActionList.LeadingVisual>
            <LockIcon size={16} />
          </ActionList.LeadingVisual>
          <span className="text-small color-fg-muted" data-testid="private-blocking-notice-modal">
            Private issues are hidden.
          </span>
        </ActionList.Item>
      )}
    </RelationshipsListAllViewPaginated>
  )
}
